/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, useEffect, useState } from 'react';
import { MenuProps, notification } from 'antd';

import { IComponentProps, ILanguage } from 'interfaces/queries/language';
import usePreferredLang from './preferredLang';

import 'dayjs/locale/fr';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import dayjs from 'dayjs';
import {
  fetchTranslation,
  requestSupportedLanguage,
  setDefaultLanguage,
} from 'queries/languages/lang';
import React from 'react';
import { capitalize } from 'utils/capitalizeWords';
import { requestChangeLanguage } from 'queries/auth/user';
import Toast from 'components/shared/Alert';

dayjs.extend(localizedFormat);
dayjs.locale('en');

const refreshToken = localStorage.getItem('refreshToken');

interface LanguageContextProps {
  translate: (_text?: string, _index?: number | string) => string;
  menuProps: MenuProps;
  currentLang: ICurrentLang;
  isLoading: boolean;
  isLoadingLanguage: boolean;
  currentLanguage: string | null;
  formatDate: (_date: string) => any;
  loadChangeLang: boolean;
}

interface ICurrentLang {
  key: number | string;
  label: string;
  icon: React.ReactNode;
}

interface LanguagePayload {
  lang: string;
  device_id: string;
}

const generateDeviceId = (): string => {
  let deviceId = localStorage.getItem('deviceId');

  if (!deviceId) {
    deviceId = 'dev_' + crypto.randomUUID();
    localStorage.setItem('deviceId', deviceId);
  }

  return deviceId;
};

const defaultLang = 'en';
const LanguageContext = createContext<LanguageContextProps | null>(null);

const LanguageProvider: React.FC<IComponentProps> = ({ children }) => {
  const [deviceId, setDeviceId] = useState<string>('');
  const [api, contectHolder] = notification.useNotification();
  const {
    currentLanguage,
    savePreferredLang,
    currentLanguageData,
    savePreferredLangData,
  } = usePreferredLang();

  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    currentLanguage || defaultLang
  );

  const { languages, isLoading } = requestSupportedLanguage();

  const {
    isSuccess: isSetLangSuccess,
    isLoadingLanguage: loadSetLang,
    submitData: requestPayload,
  } = setDefaultLanguage();

  const {
    loadChangeLang,
    changeLang,
    changeLangResponse,
    successChangeLang,
    errorChangeLang,
    clearChangeLangResponse,
  } = requestChangeLanguage();

  const { isSuccess, languageData, isLoadingLanguage } = fetchTranslation();

  const handleMenuClick: MenuProps['onClick'] = (value) => {
    savePreferredLang(value.key);
  };

  const items: MenuProps['items'] | undefined = languages?.map(
    (item: ILanguage) => ({
      key: item.language,
      label: capitalize(item.language),
      value: item.id,
    })
  );

  const currentLang: any =
    items && items.find((lang: any) => lang?.key === selectedLanguage);

  useEffect(() => {
    const id = generateDeviceId();
    setDeviceId(id);
  }, []);

  useEffect(() => {
    if (!refreshToken && selectedLanguage && selectedLanguage != null) {
      requestPayload({ lang: selectedLanguage, device_id: deviceId });
    }
  }, [selectedLanguage]);

  useEffect(() => {
    if (refreshToken && selectedLanguage && selectedLanguage != null) {
      requestPayload({ lang: selectedLanguage, device_id: deviceId });
      changeLang({ language_id: currentLang?.value });
    }
  }, [selectedLanguage]);

  useEffect(() => {
    const checkDefaultLang = defaultLang != currentLanguage;
    if (checkDefaultLang && isSuccess) {
      const langData = JSON.stringify(languageData);
      savePreferredLangData(langData);
    }

    if (currentLanguage) {
      setSelectedLanguage(currentLanguage);
    }
  }, [currentLang, currentLanguage, languageData]);

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  const translate = (text = ''): string => {
    if (defaultLang === currentLanguage) {
      return text;
    }

    if (!currentLanguageData) {
      return text;
    }

    try {
      const preferredLangData: any = currentLanguageData.find(
        (t: any) => t[defaultLang].toLowerCase() === text.toLowerCase()
      );

      return !preferredLangData ? text : preferredLangData[currentLang.key];
    } catch (error) {
      return text;
    }
  };

  dayjs.extend(localizedFormat);
  dayjs.locale(currentLang?.key);

  const formatDate = (dateString: string) => {
    const datePart = dateString?.split(' - ')[0];
    return dayjs(datePart).format('LL');
  };

  useEffect(() => {
    if (refreshToken && successChangeLang) {
      Toast(api, 'Success', changeLangResponse);
    }
  }, [changeLangResponse, successChangeLang, errorChangeLang]);

  return (
    <LanguageContext.Provider
      value={{
        translate,
        menuProps,
        currentLang,
        isLoading,
        isLoadingLanguage,
        currentLanguage,
        formatDate,
        loadChangeLang,
      }}
    >
      {contectHolder}
      {children}
    </LanguageContext.Provider>
  );
};

const useLanguage = () => {
  const languageContext = useContext(LanguageContext);
  if (!languageContext) {
    throw new Error('Platform configuration not available');
  }
  return languageContext;
};

export { useLanguage, LanguageProvider };
